import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  makeStyles,
  Divider,
  Button,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { MarkdownContent } from '@backstage/core-components';
import { AIPredictionResponse } from '../../types';

const useStyles = makeStyles(theme => ({
  aiCard: {
    border: `2px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#cbd5e0'}`,
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.type === 'dark' ? '#1a202c' : '#f7fafc',
    marginBottom: theme.spacing(3),
  },
  aiHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  aiIcon: {
    fontSize: '1.5rem',
    marginRight: theme.spacing(1),
    color: theme.palette.type === 'dark' ? '#a0aec0' : '#4a5568',
  },
  aiTitle: {
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#2d3748',
    display: 'flex',
    alignItems: 'center',
  },
  aiContentContainer: {
    position: 'relative',
  },
  aiContentCollapsed: {
    maxHeight: 300,
    overflow: 'hidden',
    position: 'relative',
  },
  aiContentExpanded: {
    overflow: 'visible',
  },
  fadeOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 60,
    background: `linear-gradient(transparent, ${theme.palette.type === 'dark' ? '#1a202c' : '#f7fafc'})`,
    pointerEvents: 'none',
  },
  aiContent: {
    lineHeight: 1.6,
    color: theme.palette.text.primary,
    '& p': {
      marginBottom: theme.spacing(1),
    },
    '& ul, & ol': {
      marginLeft: theme.spacing(2),
      marginBottom: theme.spacing(1),
    },
    '& li': {
      marginBottom: theme.spacing(0.5),
    },
    '& strong': {
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    '& code': {
      backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#f1f3f4',
      padding: '2px 4px',
      borderRadius: 4,
      fontSize: '0.875rem',
      fontFamily: 'monospace',
    },
    '& pre': {
      backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#f1f3f4',
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      overflow: 'auto',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
    },
  },
  expandButton: {
    marginTop: theme.spacing(1),
    width: '100%',
    justifyContent: 'center',
    textTransform: 'none',
    color: theme.palette.type === 'dark' ? '#a0aec0' : '#4a5568',
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#cbd5e0'}`,
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#e2e8f0',
      border: `1px solid ${theme.palette.type === 'dark' ? '#718096' : '#a0aec0'}`,
    },
  },
  aiMeta: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#e2e8f0'}`,
  },
  metaChip: {
    fontSize: '0.75rem',
    height: 24,
    backgroundColor: theme.palette.type === 'dark' ? '#2d3748' : '#e2e8f0',
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#2d3748',
    border: `1px solid ${theme.palette.type === 'dark' ? '#4a5568' : '#cbd5e0'}`,
  },
  costInfo: {
    color: theme.palette.type === 'dark' ? '#a0aec0' : '#718096',
    fontSize: '0.75rem',
    marginTop: theme.spacing(1),
  },
}));

interface AISuggestionDisplayProps {
  suggestion: AIPredictionResponse;
}

export const AISuggestionDisplay: React.FC<AISuggestionDisplayProps> = ({
  suggestion,
}) => {
  const classes = useStyles();
  const [isExpanded, setIsExpanded] = useState(false);

  // Reset expand state when suggestion changes
  useEffect(() => {
    setIsExpanded(false);
  }, [suggestion.id]);

  // Check if content is long enough to need expansion
  const isLongContent = suggestion.response.length > 800;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    });
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <Card className={classes.aiCard} variant="outlined">
      <CardContent>
        <Box className={classes.aiHeader}>
          <Typography className={classes.aiIcon}>🤖</Typography>
          <Typography variant="h6" className={classes.aiTitle}>
            AI Error Analysis
          </Typography>
        </Box>

        <Box className={classes.aiContentContainer}>
          <Box
            className={
              isLongContent && !isExpanded
                ? classes.aiContentCollapsed
                : classes.aiContentExpanded
            }
          >
            <Box className={classes.aiContent}>
              <MarkdownContent content={suggestion.response} />
            </Box>
          </Box>
          
          {isLongContent && !isExpanded && (
            <Box className={classes.fadeOverlay} />
          )}
        </Box>

        {isLongContent && (
          <Button
            className={classes.expandButton}
            onClick={handleToggleExpand}
            endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {isExpanded ? 'Show Less' : 'Show More'}
          </Button>
        )}

        <Divider />

        <Box className={classes.aiMeta}>
          <Chip
            label={`${suggestion.provider}: ${suggestion.model}`}
            size="small"
            variant="outlined"
            className={classes.metaChip}
          />
          <Chip
            label={`${suggestion.tokens_used.toLocaleString()} tokens`}
            size="small"
            variant="outlined"
            className={classes.metaChip}
          />
          <Chip
            label={formatDate(suggestion.created_at)}
            size="small"
            variant="outlined"
            className={classes.metaChip}
          />
        </Box>

      </CardContent>
    </Card>
  );
};